import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setError("Missing checkout session"); return; }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setStatus(response.data);
        if (response.data.payment_status === "paid") {
          if (!cleared.current) { clear(); localStorage.removeItem("mellowj_pending_cart"); cleared.current = true; }
          clearInterval(timer);
        } else if (attempts > 15) clearInterval(timer);
      } catch (err) {
        setError("We could not verify this payment yet.");
        clearInterval(timer);
      }
    }, 1800);
    return () => clearInterval(timer);
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="spotlight flex min-h-screen items-center bg-onyx px-4 pt-24">
      <Seo title="Payment Complete" description="Your mellowjmedia payment was received and your digital download links are ready." />
      <div className="mx-auto w-full max-w-4xl border border-gold/20 bg-carbon p-8 text-center sm:p-12">
        {error ? <p data-testid="payment-error-message" className="text-red-200">{error}</p> : !status || status.payment_status !== "paid" ? (
          <div data-testid="payment-processing-state" className="py-12">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-gold" />
            <h1 className="mt-6 font-display text-4xl text-white">Confirming your payment.</h1>
            <p className="mt-3 text-zinc-500">Stripe is completing the transaction. This usually takes a few seconds.</p>
          </div>
        ) : (
          <div data-testid="payment-complete-state">
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.42em] text-gold">Payment received</p>
            <h1 className="mt-4 font-display text-5xl font-light text-white">Your originals are ready.</h1>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-zinc-500">Download links remain active for 7 days. If you checked out while signed in, they are also saved to your dashboard.</p>
            {status.email_sent && <p data-testid="success-email-confirmation" className="mt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-gold">A copy of these links was emailed to you</p>}
            <div className="mt-10 grid gap-4 text-left">
              {status.downloads.map((download) => (
                <a key={download.download_url} data-testid={`success-download-${download.item_type}`} href={`${process.env.REACT_APP_BACKEND_URL}${download.download_url}`} className="flex items-center justify-between gap-4 border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-gold/50">
                  <span className="font-lux text-white">{download.title}</span>
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold"><Download className="h-4 w-4" /> Download</span>
                </a>
              ))}
            </div>
            <Link data-testid="success-view-dashboard-button" to="/dashboard" className="mt-8 inline-flex border border-gold/40 px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] text-gold hover:bg-gold hover:text-onyx">View dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}
